/*
 * File: CookieBar.jsx
 * Project: our-wave-stories-app
 *
 * Created by Brendan Michaelsen on January 24, 2022 at 9:59 PM
 * Copyright © 2022 Our Wave, Inc. All rights reserved.
 *
 * Last Modified: May 31, 2025 at 10:49 PM
 * Modified By: Brendan Michaelsen
 */

/**
 * Imports
 */

// Modules
import React, {
	useState, useEffect, useRef, useMemo
} from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useWindowResize } from 'beautiful-react-hooks';
import { Trans, useTranslation } from 'react-i18next';

// Utilities
import { setCookie } from '../../utilities/cookies';

// Slices
import { updateConsent } from '../../store/slices/consent/consent.slice';
import { updateCookieBarHeight } from '../../store/slices/dimensions/dimensions.slice';

// Components
import { Padding } from '../Padding';
import { Typography } from '../Typography';
import { Button } from '../Button';
import { LocaleLink } from '../LocaleLink';

// Constants
import {
	CONSENT_COOKIE_EXPIRATION,
	CONSENT_ACTION_TAKEN_COOKIE,
	CONSENT_PERFORMANCE_ENABLED_COOKIE,
	CONSENT_ANALYTICS_ENABLED_COOKIE,
	CONSENT_TARGETING_ENABLED_COOKIE,
	AVAILABLE_I18N_NAMESPACES
} from '../../../constants/base.constants';

// Styles
import * as S from './CookieBar.styles';


/**
 * Component
 */


export const CookieBar = () => {

	// Get actions from hooks
	const dispatch = useDispatch();

	// Create reference for cookie bar
	const [showBar, setShowBar] = useState(true);
	const [barHeight, setBarHeight] = useState(0);
	const outerRef = useRef();

	// Get current locale from hook
	const locale = useSelector((state) => state.locale.value);

	// Get partner from hook
	const partner = useSelector((state) => state.partner.value);

	// Get legal urls
	const cookieStatementUrl = useMemo(() => (partner?.legal?.links?.cookie_statement ? partner?.legal?.links?.cookie_statement : `https://www.ourwave.org${locale.localePath}/cookie-statement`), [partner, locale]);
	const hasInternalLegalPages = useMemo(() => partner?.legal?.has_internal_legal_pages, [partner]);

	// Get translation hook
	const [t] = useTranslation(AVAILABLE_I18N_NAMESPACES.CLIENT);

	// Handle actions on bar height change
	useEffect(() => {

		// Update state
		dispatch(updateCookieBarHeight(showBar ? barHeight : 0));

	}, [barHeight, showBar, dispatch]);

	// Handle actions on component load
	useEffect(() => {

		// Set bar height
		setBarHeight(outerRef.current.clientHeight);
	}, []);

	// Handle actions on window resize
	useWindowResize(() => {

		// Set bar height
		setBarHeight(outerRef.current.clientHeight);
	});

	// Get current consent state from hook
	const currentConsent = useSelector((state) => state.consent.currentValue);

	// Accept cookies function
	const acceptAllCookies = () => {

		// Set cookie values
		setCookie(CONSENT_ACTION_TAKEN_COOKIE, 'true', partner, CONSENT_COOKIE_EXPIRATION);
		setCookie(CONSENT_PERFORMANCE_ENABLED_COOKIE, 'true', partner, CONSENT_COOKIE_EXPIRATION);
		setCookie(CONSENT_ANALYTICS_ENABLED_COOKIE, 'true', partner, CONSENT_COOKIE_EXPIRATION);
		setCookie(CONSENT_TARGETING_ENABLED_COOKIE, 'true', partner, CONSENT_COOKIE_EXPIRATION);

		// Hide cookie bar
		setShowBar(false);

		// Update consent state
		setTimeout(() => {
			dispatch(updateConsent({
				consent_action_taken: true,
				performance_enabled: true,
				analytics_enabled: true,
				targeting_enabled: true
			}));
		}, 400);
	};

	// Customize settings function
	const customizeSettings = () => {

		// Set cookie values
		setCookie(CONSENT_ACTION_TAKEN_COOKIE, 'true', partner, CONSENT_COOKIE_EXPIRATION);

		// Hide cookie bar
		setShowBar(false);

		// Update consent state
		dispatch(updateConsent({
			...currentConsent,
			consent_action_taken: true,
		}));

		// Navigate to cookie policy
		window.open(`${cookieStatementUrl}#settings`, '_blank');
	};

	// Return component
	return (
		<S.CookieBar ref={outerRef} $showBar={showBar} $barHeight={barHeight} $partner={partner}>
			<Padding left right>
				<S.TextContainer>
					<Typography tag="p">
						<Trans t={t} i18nKey="cookieBar.instructions">
							{{ partnerName: partner.name }}
							{' '}
							uses cookies to provide you with a more personalized experience. Please read our
							{' '}
							<LocaleLink to={cookieStatementUrl} target={!hasInternalLegalPages ? '_blank' : null}>cookie policy</LocaleLink>
							{' '}
							for more information.
						</Trans>
					</Typography>
				</S.TextContainer>
				<S.ButtonContainer>
					<Button onClick={acceptAllCookies}>
						<Typography className="expandedState">
							<Trans t={t} i18nKey="cookieBar.acceptButton">
								Accept All Cookies
							</Trans>
						</Typography>
						<Typography className="condensedState">
							<Trans t={t} i18nKey="cookieBar.acceptButtonMobile">
								Accept All
							</Trans>
						</Typography>
					</Button>
					<Button variant="text" className="rightGutter" onClick={customizeSettings}>
						<Typography className="expandedState">
							<Trans t={t} i18nKey="cookieBar.settingsButton">
								Customize Settings
							</Trans>
						</Typography>
						<Typography className="condensedState">
							<Trans t={t} i18nKey="cookieBar.settingsButtonMobile">
								Settings
							</Trans>
						</Typography>
					</Button>
				</S.ButtonContainer>
			</Padding>
		</S.CookieBar>
	);
};


/**
 * Configuration
 */

CookieBar.displayName = 'CookieBar';
CookieBar.propTypes = {};
CookieBar.defaultProps = {};
